import { create } from 'zustand';
import { applyNodeChanges, applyEdgeChanges, addEdge } from 'reactflow';
import { produce } from 'immer';
import toast from 'react-hot-toast';
import { logger } from '../utils/logger';

const useFlowStore = create((set, get) => ({
  // Current model state
  currentModel: null,
  modelName: '',
  modelType: 'relational',
  modelDescription: '',
  modelBusinessRules: '',
  
  // Flow state
  nodes: [],
  edges: [],
  viewport: { x: 0, y: 0, zoom: 1 },
  
  // Global tags library
  globalTags: [],

  // Global domains library
  globalDomains: [],

  // User-Defined Types (shared across all models, loaded once after login)
  userTypes: [],
  setUserTypes: (types) => set({ userTypes: types }),
  
  // UI state
  selectedNode: null,
  selectedNodes: [], // Multiple selected nodes
  selectedEdge: null,
  highlightedEdges: [], // Edges to highlight when a node is selected
  highlightedNodes: [], // Nodes to highlight when an edge is selected
  isDirty: false,
  pendingConnection: null, // Connection waiting for cardinality selection
  isLoading: false, // Flag to prevent marking as dirty during load
  theme: 'light',
  showFieldDescriptions: false, // Flag to show/hide field descriptions in nodes
  isMultiSelectMode: false, // Flag to enable/disable multi-select mode
  /** Full-screen entity editor (EntityEditModal), also opened from node toolbar */
  showEntityEditModal: false,
  /** Consumed by Properties to run the same delete-confirm flow as the panel footer */
  pendingDeleteEntityId: null,
  /** After toolbar requests an action, Properties opens the matching modal when nodeData is ready */
  entityToolbarPending: null,

  // Diagram Filter State
  diagramFilter: {
    active: false,
    searchQuery: '',
    entityTypes: [],
    tags: [],
    domain: '',
    schema: '',
    hideUnmatched: false
  },

  setDiagramFilter: (filterUpdate) => {
    set(produce((state) => {
      state.diagramFilter = {
        ...state.diagramFilter,
        ...(typeof filterUpdate === 'function' ? filterUpdate(state.diagramFilter) : filterUpdate)
      };
      const f = state.diagramFilter;
      f.active = !!(
        (f.searchQuery && f.searchQuery.trim()) ||
        (f.entityTypes && f.entityTypes.length > 0) ||
        (f.tags && f.tags.length > 0) ||
        (f.domain && f.domain.trim()) ||
        (f.schema && f.schema.trim())
      );
    }));
  },

  clearDiagramFilter: () => {
    set(produce((state) => {
      state.diagramFilter = {
        active: false,
        searchQuery: '',
        entityTypes: [],
        tags: [],
        domain: '',
        schema: '',
        hideUnmatched: false
      };
    }));
  },

  renameSchema: (oldSchema, newSchema) => {
    const trimmedOld = (oldSchema || '').trim();
    const trimmedNew = (newSchema || '').trim();
    if (!trimmedOld || trimmedOld === trimmedNew) return;
    get().saveToHistory();
    set(produce((state) => {
      state.nodes.forEach((node) => {
        if (node.data && (node.data.schema || '').trim() === trimmedOld) {
          node.data.schema = trimmedNew;
        }
      });
      if (state.selectedNode && (state.selectedNode.data?.schema || '').trim() === trimmedOld) {
        state.selectedNode.data.schema = trimmedNew;
      }
      state.isDirty = true;
    }));
  },

  // History state (Undo/Redo)
  history: [], // Array of snapshots (max 10)
  historyIndex: -1, // Current position in history (-1 means no history)
  
  // Actions
  setCurrentModel: (model) => {
    logger.log('=== LOADING MODEL ===');
    logger.log('Model:', model);

    if (!model) return;

    // Check if graph payload is wrapped inside model.data (D1 / database structure)
    const payload = (model.data && typeof model.data === 'object' && (model.data.nodes || model.data.edges))
      ? model.data
      : model;

    const currentNodes = get().nodes || [];
    const currentEdges = get().edges || [];

    const rawNodes = payload.nodes || model.nodes || (currentNodes.length > 0 ? currentNodes : []);
    const rawEdges = payload.edges || model.edges || (currentEdges.length > 0 ? currentEdges : []);
    const modelViewport = payload.viewport || model.viewport || get().viewport || { x: 0, y: 0, zoom: 1 };
    const modelBusinessRules = payload.businessRules || model.businessRules || model.modelBusinessRules || get().modelBusinessRules || '';
    const modelGlobalTags = payload.globalTags || model.globalTags || get().globalTags || [];
    const modelGlobalDomains = payload.globalDomains || model.globalDomains || get().globalDomains || [];
    
    // Ensure all edges have cardinalityLocked: true (for backward compatibility)
    const edgesWithLock = rawEdges.map(edge => ({
      ...edge,
      data: {
        ...edge.data,
        cardinalityLocked: edge.data?.cardinalityLocked !== undefined ? edge.data.cardinalityLocked : true
      }
    }));
    
    // Distribute targetHandles for edges connecting to the same target
    const edgesWithHandles = edgesWithLock.map(edge => {
      if (edge.targetHandle) return edge;
      const targetEdgeIndex = edgesWithLock
        .filter(e => e.target === edge.target)
        .findIndex(e => e.id === edge.id);
      return {
        ...edge,
        targetHandle: `target-${targetEdgeIndex >= 0 ? targetEdgeIndex : 0}`
      };
    });

    // Build the final nodes array: prefer server domains when present, fall back to local
    const nodesWithDomains = rawNodes.map(node => {
      const currentNode = currentNodes.find(n => n.id === node.id);
      return {
        ...node,
        data: {
          ...node.data,
          domains: node.data?.domains?.length > 0
            ? node.data.domains
            : currentNode?.data?.domains || []
        }
      };
    });

    // Keep selectedNode reference in sync with the refreshed nodes
    const currentSelectedNode = get().selectedNode;
    const updatedSelectedNode = currentSelectedNode
      ? nodesWithDomains.find(n => n.id === currentSelectedNode.id) || null
      : null;

    set({ 
      currentModel: model,
      modelName: model.name || get().modelName || '',
      modelType: model.type || get().modelType || 'relational',
      modelDescription: model.description || get().modelDescription || '',
      modelBusinessRules: modelBusinessRules,
      nodes: nodesWithDomains,
      edges: edgesWithHandles,
      selectedNode: updatedSelectedNode,
      viewport: modelViewport,
      globalTags: modelGlobalTags,
      globalDomains: modelGlobalDomains,
      isDirty: false,
      isLoading: true,
      history: [], // Clear history when loading a model
      historyIndex: -1,
      showEntityEditModal: false,
      pendingDeleteEntityId: null,
      entityToolbarPending: null,
    });
    
    // Reset loading flag and initialize history
    setTimeout(() => {
      set({ isLoading: false });
      // Save initial state to history
      get().saveToHistory();
    }, 500);
  },
  
  setModelName: (name) => {
    get().saveToHistory();
    set({ modelName: name, isDirty: true });
  },
  setModelType: (type) => {
    get().saveToHistory();
    set({ modelType: type, isDirty: true });
  },
  setModelDescription: (description) => {
    get().saveToHistory();
    set({ modelDescription: description, isDirty: true });
  },
  setModelBusinessRules: (rules) => {
    get().saveToHistory();
    set({ modelBusinessRules: rules, isDirty: true });
  },
  
  onNodesChange: (changes) => {
    const state = get();
    const updatedNodes = applyNodeChanges(changes, state.nodes);
    
    // Check if selection changed
    const selectionChange = changes.find(c => c.type === 'select');
    let newHighlightedEdges = state.highlightedEdges;
    let newSelectedNodes = state.selectedNodes;
    
    if (selectionChange) {
      // Find all selected nodes (support for multiple selection)
      const selectedNodes = updatedNodes.filter(n => n.selected);
      newSelectedNodes = selectedNodes;
      
      if (selectedNodes.length === 1) {
        // Single selection - highlight edges from this node
        newHighlightedEdges = state.edges
          .filter(edge => edge.source === selectedNodes[0].id)
          .map(edge => edge.id);
      } else if (selectedNodes.length > 1) {
        // Multiple selection - highlight all edges from selected nodes
        const selectedIds = selectedNodes.map(n => n.id);
        newHighlightedEdges = state.edges
          .filter(edge => selectedIds.includes(edge.source))
          .map(edge => edge.id);
      } else {
        // No node selected, clear highlights
        newHighlightedEdges = [];
      }
    }
    
    set({
      nodes: updatedNodes,
      selectedNodes: newSelectedNodes,
      highlightedEdges: newHighlightedEdges,
      // Don't mark as dirty if we're loading or if it's just selection/dimension changes
      isDirty: state.isLoading ? false : (state.isDirty || changes.some(c => 
        c.type === 'add' || c.type === 'remove' || c.type === 'reset' || 
        (c.type === 'position' && c.dragging === false)
      ))
    });
  },
  
  onEdgesChange: (changes) => {
    const state = get();
    const safeChanges = changes.filter(change => {
      if (change.type === 'remove') {
        const edge = state.edges.find(e => e.id === change.id);
        if (edge) {
          const targetNode = state.nodes.find(n => n.id === edge.target);
          const partitioning = targetNode?.data?.partitioning;
          if (partitioning && partitioning.type && partitioning.type !== 'none') {
            const lockedCols = partitioning.type === 'hash'
              ? (partitioning.columns || [])
              : (partitioning.column ? [partitioning.column] : (partitioning.columns || []));
            const sourceNode = state.nodes.find(n => n.id === edge.source);
            const srcName = sourceNode?.data?.name || 'entity';
            const fkName = (edge.data?.fkAlias && String(edge.data.fkAlias).trim()) || `${srcName.toLowerCase()}_id`;
            if (lockedCols.some(c => c.toLowerCase() === fkName.toLowerCase())) {
              return false; // Prevent removal via keyboard / direct change
            }
          }
        }
      }
      return true;
    });

    const updatedEdges = applyEdgeChanges(safeChanges, state.edges);
    
    set({
      edges: updatedEdges,
      // Don't mark as dirty if we're loading or if it's just selection changes
      isDirty: state.isLoading ? false : (state.isDirty || safeChanges.some(c => 
        c.type === 'add' || c.type === 'remove' || c.type === 'reset'
      ))
    });
  },
  
  onConnect: (connection) => {
    const state = get();
    const currentNodes = state.nodes;
    
    // VALIDATION: Check if source node has a Primary Key
    const sourceNode = currentNodes.find(n => n.id === connection.source);
    const targetNode = currentNodes.find(n => n.id === connection.target);
    
    if (!sourceNode || !targetNode) {
      logger.error('[Flow] Source or target node not found');
      return;
    }
    
    const sourcePK = sourceNode.data.fields?.find(f => f.isPrimaryKey);
    
    if (!sourcePK) {
      logger.warn(`[Flow] Cannot create relationship: "${sourceNode.data.name}" has no Primary Key`);
      
      // Show toast notification
      toast.error(
        `Cannot create relationship!\n\nThe table "${sourceNode.data.name}" must have a Primary Key field.\n\nPlease add a Primary Key before creating relationships.`,
        {
          duration: 5000,
          position: 'top-center',
          style: {
            background: '#fee2e2',
            color: '#991b1b',
            fontWeight: 'bold',
            maxWidth: '500px'
          }
        }
      );
      
      return; // Cancel relationship creation
    }
    
    // Save connection as pending - modal will handle confirmation
    set({ 
      pendingConnection: {
        connection,
        sourceNode,
        targetNode
      }
    });
  },
  
  confirmConnection: (cardinality, label, fkAlias = '') => {
    get().saveToHistory();
    const state = get();
    const { pendingConnection, edges: currentEdges, nodes } = state;
    
    if (!pendingConnection) return;
    
    const { connection, sourceNode, targetNode } = pendingConnection;

    const existingConnectionsToTarget = currentEdges.filter(
      (edge) => edge.target === connection.target
    ).length;
    const existingConnectionsFromSource = currentEdges.filter(
      (edge) => edge.source === connection.source
    ).length;

    const targetHandle = `target-${existingConnectionsToTarget}`;
    const sourceHandle = `source-${existingConnectionsFromSource}`;

    // Create junction table for N:N relationships
    let junctionTable = null;
    if (cardinality === 'N:N') {
      const sourceName = sourceNode?.data?.name || 'table1';
      const targetName = targetNode?.data?.name || 'table2';
      const junctionTableName = label || `${sourceName}_${targetName}`;
      
      // Find PKs from both tables
      const sourcePK = sourceNode?.data?.fields?.find(f => f.isPrimaryKey);
      const targetPK = targetNode?.data?.fields?.find(f => f.isPrimaryKey);
      
      // Create junction table structure
      junctionTable = {
        name: junctionTableName,
        fields: [
          {
            name: 'id',
            type: 'INTEGER',
            nullable: false,
            isPrimaryKey: true,
            isForeignKey: false,
            autoIncrement: true,
            description: 'Junction table primary key'
          },
          {
            name: `${sourceName}_${sourcePK?.name || 'id'}`,
            type: sourcePK?.type || 'INTEGER',
            nullable: false,
            isPrimaryKey: false,
            isForeignKey: true,
            autoIncrement: false,
            description: `Foreign key to ${sourceName}`
          },
          {
            name: `${targetName}_${targetPK?.name || 'id'}`,
            type: targetPK?.type || 'INTEGER',
            nullable: false,
            isPrimaryKey: false,
            isForeignKey: true,
            autoIncrement: false,
            description: `Foreign key to ${targetName}`
          }
        ],
        indexes: [
          {
            name: `idx_${junctionTableName}_fks`,
            type: 'Non-Clustered',
            columns: [
              {
                fieldName: `${sourceName}_${sourcePK?.name || 'id'}`,
                order: 'ASC'
              },
              {
                fieldName: `${targetName}_${targetPK?.name || 'id'}`,
                order: 'ASC'
              }
            ]
          }
        ],
        description: `Junction table for ${sourceName} and ${targetName} many-to-many relationship`,
        sourceTable: sourceName,
        targetTable: targetName,
        sourcePK: sourcePK?.name || 'id',
        targetPK: targetPK?.name || 'id'
      };
    }
    
    // Create the new edge with the selected cardinality
    const newEdge = {
      ...connection,
      type: 'default',
      sourceHandle,
      targetHandle,
      data: {
        cardinality: cardinality,
        label: label || '',
        fkAlias: (fkAlias && String(fkAlias).trim()) || '',
        cardinalityLocked: true, // Lock cardinality after creation
        junctionTable: junctionTable // Store junction table if N:N
      }
    };
    
    set({
      edges: addEdge(newEdge, currentEdges),
      isDirty: true,
      pendingConnection: null
    });
  },
  
  cancelConnection: () => {
    set({ pendingConnection: null });
  },
  
  // Direct edge creation (for imports, without modal)
  createEdgeDirectly: (connection, cardinality = '1:N', label = '', fkAlias = '') => {
    const state = get();
    const currentEdges = state.edges;
    
    const existingConnectionsToTarget = currentEdges.filter(
      edge => edge.target === connection.target
    ).length;
    const existingConnectionsFromSource = currentEdges.filter(
      edge => edge.source === connection.source
    ).length;

    const targetHandle = `target-${existingConnectionsToTarget}`;
    const sourceHandle = `source-${existingConnectionsFromSource}`;

    const newEdge = {
      ...connection,
      type: 'default',
      sourceHandle,
      targetHandle,
      data: {
        cardinality: cardinality,
        label: label || '',
        fkAlias: (fkAlias && String(fkAlias).trim()) || '',
        cardinalityLocked: true, // Always locked
        ...(connection.data || {}) // Preserve any existing data
      }
    };
    
    set({
      edges: addEdge(newEdge, currentEdges),
      isDirty: true
    });
  },
  
  addNode: (node) => {
    get().saveToHistory();
    set(produce((state) => {
      state.nodes.push(node);
      state.isDirty = true;
    }));
  },

  duplicateNode: (nodeId, newName) => {
    get().saveToHistory();
    set(produce((state) => {
      const originalNode = state.nodes.find(n => n.id === nodeId);
      
      if (!originalNode) {
        logger.error('Node not found for duplication:', nodeId);
        return;
      }

      // Generate new ID
      const newId = `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      
      // Calculate offset position (place duplicated node to the right and down)
      const offsetX = 300;
      const offsetY = 50;
      
      // Create duplicated node with new name, ID, and position
      // Copy fields and tags, but NOT relationships (edges are not duplicated)
      const duplicatedNode = {
        ...originalNode,
        id: newId,
        position: {
          x: originalNode.position.x + offsetX,
          y: originalNode.position.y + offsetY
        },
        data: {
          ...originalNode.data,
          name: newName,
          // Deep copy fields array
          fields: originalNode.data.fields?.map(field => ({ ...field })) || [],
          // Deep copy tags array
          tags: originalNode.data.tags?.map(tag => ({ ...tag })) || []
        },
        selected: false
      };
      
      // Add the duplicated node
      state.nodes.push(duplicatedNode);
      state.isDirty = true;
      
      // Note: Relationships (edges) are NOT duplicated as per requirements
      logger.log('Node duplicated:', {
        original: originalNode.data.name,
        new: newName,
        id: newId,
        fieldsCount: duplicatedNode.data.fields?.length,
        tagsCount: duplicatedNode.data.tags?.length
      });
    }));
  },

  setNodes: (nodes) => {
    set(produce((state) => {
      state.nodes = nodes;
      state.isDirty = true;
      if (state.selectedNode) {
        const updated = nodes.find((n) => n.id === state.selectedNode.id);
        if (updated) state.selectedNode = updated;
      }
      if (state.selectedNodes?.length) {
        state.selectedNodes = state.selectedNodes.map(
          (n) => nodes.find((x) => x.id === n.id) || n
        );
      }
    }));
  },

  setEdges: (edges) => {
    set({ edges });
  },
  
  updateNode: (nodeId, data) => {
    get().saveToHistory();
    set(produce((state) => {
      const node = state.nodes.find(n => n.id === nodeId);
      if (node) {
        node.data = { ...node.data, ...data };
        state.isDirty = true;
        // Update selectedNode if it's the node being updated
        if (state.selectedNode?.id === nodeId) {
          state.selectedNode = node;
        }
      }
    }));
  },

  // Toggle collapsed state without polluting undo history
  toggleNodeCollapsed: (nodeId) => {
    set(produce((state) => {
      const node = state.nodes.find(n => n.id === nodeId);
      if (node) {
        node.data = { ...node.data, collapsed: !node.data.collapsed };
        state.isDirty = true;
      }
    }));
  },
  
  deleteNode: (nodeId) => {
    get().saveToHistory();
    set(produce((state) => {
      state.nodes = state.nodes.filter(n => n.id !== nodeId);
      state.edges = state.edges.filter(e => e.source !== nodeId && e.target !== nodeId);
      state.isDirty = true;
      if (state.selectedNode?.id === nodeId) {
        state.selectedNode = null;
      }
      // Update selectedNodes if it contains the deleted node
      state.selectedNodes = state.selectedNodes.filter(n => n.id !== nodeId);
    }));
  },

  deleteMultipleNodes: (nodeIds) => {
    get().saveToHistory();
    set(produce((state) => {
      // Filter out all nodes with IDs in the array
      state.nodes = state.nodes.filter(n => !nodeIds.includes(n.id));
      
      // Remove all edges connected to any of the deleted nodes
      state.edges = state.edges.filter(e => 
        !nodeIds.includes(e.source) && !nodeIds.includes(e.target)
      );
      
      state.isDirty = true;
      
      // Clear selections
      state.selectedNode = null;
      state.selectedNodes = [];
      state.selectedEdge = null;
      state.highlightedEdges = [];
      
      logger.log('Multiple nodes deleted:', {
        count: nodeIds.length,
        ids: nodeIds
      });
    }));
  },
  
  updateEdge: (edgeId, data) => {
    get().saveToHistory();
    set(produce((state) => {
      const edge = state.edges.find(e => e.id === edgeId);
      if (edge) {
        edge.data = { ...edge.data, ...data };
        state.isDirty = true;
      }
    }));
  },
  
  deleteEdge: (edgeId) => {
    const state = get();
    const edge = state.edges.find(e => e.id === edgeId);
    if (edge) {
      const targetNode = state.nodes.find(n => n.id === edge.target);
      const partitioning = targetNode?.data?.partitioning;
      if (partitioning && partitioning.type && partitioning.type !== 'none') {
        const lockedCols = partitioning.type === 'hash'
          ? (partitioning.columns || [])
          : (partitioning.column ? [partitioning.column] : (partitioning.columns || []));
        const sourceNode = state.nodes.find(n => n.id === edge.source);
        const srcName = sourceNode?.data?.name || 'entity';
        const fkName = (edge.data?.fkAlias && String(edge.data.fkAlias).trim()) || `${srcName.toLowerCase()}_id`;
        if (lockedCols.some(c => c.toLowerCase() === fkName.toLowerCase())) {
          return false; // Abort deletion
        }
      }
    }

    get().saveToHistory();
    set(produce((state) => {
      state.edges = state.edges.filter(e => e.id !== edgeId);
      state.isDirty = true;
      if (state.selectedEdge?.id === edgeId) {
        state.selectedEdge = null;
      }
    }));
    return true;
  },
  
  /**
   * Apply changes returned by the AI assistant atomically.
   * All mutations go into one history entry so a single Ctrl+Z reverts everything.
   *
   * @param {{ newNodes, modifiedNodes, newEdges }} parsedResponse
   *   - newNodes: already have id + position (from aiResponseParser)
   *   - modifiedNodes: [{ id, addFields: [...] }]
   *   - newEdges: already have source + target as node IDs
   */
  applyAiChanges: (parsedResponse) => {
    get().saveToHistory();
    set(produce((state) => {
      const { newNodes = [], modifiedNodes = [], newEdges = [] } = parsedResponse;

      // Add new nodes
      for (const node of newNodes) {
        state.nodes.push(node);
      }

      // Add fields to existing nodes
      for (const mod of modifiedNodes) {
        const node = state.nodes.find(n => n.id === mod.id);
        if (node && mod.addFields?.length) {
          node.data.fields = [...(node.data.fields || []), ...mod.addFields];
          if (state.selectedNode?.id === mod.id) {
            state.selectedNode = { ...node };
          }
        }
      }

      for (const edge of newEdges) {
        const existingToTarget = state.edges.filter(e => e.target === edge.target).length;
        const existingFromSource = state.edges.filter(e => e.source === edge.source).length;
        const newEdge = {
          ...edge,
          targetHandle: `target-${existingToTarget}`,
          sourceHandle: `source-${existingFromSource}`,
        };
        state.edges = addEdge(newEdge, state.edges);
      }

      state.isDirty = true;
    }));
  },

  setSelectedNode: (node) => set({ selectedNode: node, selectedEdge: null, selectedNodes: node ? [node] : [], highlightedNodes: [] }),
  setSelectedEdge: (edge) => set({ selectedEdge: edge, selectedNode: null, selectedNodes: [], highlightedNodes: edge ? [edge.source, edge.target] : [] }),

  clearSelection: () => set({ selectedNode: null, selectedNodes: [], selectedEdge: null, highlightedNodes: [] }),

  openEntityEditModalForNode: (nodeId) => {
    const node = get().nodes.find((n) => n.id === nodeId);
    if (!node) return;
    const state = get();
    const highlightedEdges = state.edges
      .filter((edge) => edge.source === node.id)
      .map((edge) => edge.id);
    set({
      selectedNode: node,
      selectedEdge: null,
      selectedNodes: [node],
      highlightedNodes: [],
      highlightedEdges,
      showEntityEditModal: true,
    });
  },
  openEntityEditModal: () => set({ showEntityEditModal: true }),
  closeEntityEditModal: () => set({ showEntityEditModal: false }),
  requestDeleteEntity: (nodeId) => set({ pendingDeleteEntityId: nodeId }),
  clearPendingDeleteEntity: () => set({ pendingDeleteEntityId: null }),

  /**
   * @param {string} nodeId
   * @param {'duplicate' | 'saveDomain' | 'indexes' | 'rules'} action
   */
  requestEntityToolbarAction: (nodeId, action) => {
    const node = get().nodes.find((n) => n.id === nodeId);
    if (!node) return;
    const state = get();
    const highlightedEdges = state.edges
      .filter((edge) => edge.source === node.id)
      .map((edge) => edge.id);
    set({
      selectedNode: node,
      selectedEdge: null,
      selectedNodes: [node],
      highlightedNodes: [],
      highlightedEdges,
      entityToolbarPending: { action, nodeId },
    });
  },
  clearEntityToolbarPending: () => set({ entityToolbarPending: null }),
  
  setViewport: (viewport) => set({ viewport }),
  
  resetFlow: () => {
    set({
      currentModel: null,
      userModels: [],
      modelName: '',
      modelType: 'relational',
      modelDescription: '',
      modelBusinessRules: '',
      nodes: [],
      edges: [],
      viewport: { x: 0, y: 0, zoom: 1 },
      globalTags: [],
      globalDomains: [],
      selectedNode: null,
      selectedNodes: [],
      selectedEdge: null,
      highlightedNodes: [],
      isDirty: false,
      isLoading: false,
      showEntityEditModal: false,
      pendingDeleteEntityId: null,
      entityToolbarPending: null,
    });
  },
  
  toggleTheme: () => set((state) => ({ 
    theme: state.theme === 'light' ? 'dark' : 'light' 
  })),
  
  toggleFieldDescriptions: () => set((state) => ({ 
    showFieldDescriptions: !state.showFieldDescriptions 
  })),

  toggleMultiSelectMode: () => set((state) => ({ 
    isMultiSelectMode: !state.isMultiSelectMode 
  })),
  
  setIsDirty: (isDirty) => set({ isDirty }),
  
  // Global tags management
  addGlobalTag: (tag) => {
    get().saveToHistory();
    set(produce((state) => {
      state.globalTags.push(tag);
      state.isDirty = true;
    }));
  },
  
  updateGlobalTag: (tagId, updates) => {
    get().saveToHistory();
    set(produce((state) => {
      const tag = state.globalTags.find(t => t.id === tagId);
      if (tag) {
        Object.assign(tag, updates);
        state.isDirty = true;
      }
    }));
  },
  
  deleteGlobalTag: (tagId) => {
    get().saveToHistory();
    set(produce((state) => {
      state.globalTags = state.globalTags.filter(t => t.id !== tagId);
      state.isDirty = true;
    }));
  },

  // Global domains management
  addGlobalDomain: (domain) => {
    get().saveToHistory();
    set(produce((state) => {
      state.globalDomains.push(domain);
      state.isDirty = true;
    }));
  },

  deleteGlobalDomain: (domainId) => {
    get().saveToHistory();
    set(produce((state) => {
      state.globalDomains = state.globalDomains.filter(d => d.id !== domainId);
      state.isDirty = true;
    }));
  },

  // History management (Undo)
  saveToHistory: () => {
    const state = get();
    
    // Don't save during loading or if no model
    if (state.isLoading || !state.modelName) {
      return;
    }
    
    // Create a snapshot of the current state
    const snapshot = {
      modelName: state.modelName,
      modelType: state.modelType,
      modelDescription: state.modelDescription,
      modelBusinessRules: state.modelBusinessRules,
      nodes: JSON.parse(JSON.stringify(state.nodes)), // Deep clone
      edges: JSON.parse(JSON.stringify(state.edges)), // Deep clone
      viewport: { ...state.viewport },
      globalTags: JSON.parse(JSON.stringify(state.globalTags)), // Deep clone
      globalDomains: JSON.parse(JSON.stringify(state.globalDomains)) // Deep clone
    };
    
    set(produce((state) => {
      // Remove any history after current index (if user did undo and then made a new action)
      state.history = state.history.slice(0, state.historyIndex + 1);
      
      // Add new snapshot
      state.history.push(snapshot);
      
      // Keep only last 10 snapshots
      if (state.history.length > 10) {
        state.history = state.history.slice(-10);
      }
      
      // Update history index
      state.historyIndex = state.history.length - 1;
    }));
  },
  
  undo: () => {
    const state = get();
    
    // Check if undo is possible
    if (state.historyIndex <= 0) {
      toast.error('No more actions to undo');
      return;
    }
    
    // Move to previous state
    const newIndex = state.historyIndex - 1;
    const previousState = state.history[newIndex];
    
    if (!previousState) {
      toast.error('Cannot undo: invalid history state');
      return;
    }
    
    // Restore the previous state
    set({
      modelName: previousState.modelName,
      modelType: previousState.modelType,
      modelDescription: previousState.modelDescription,
      modelBusinessRules: previousState.modelBusinessRules || '',
      nodes: JSON.parse(JSON.stringify(previousState.nodes)), // Deep clone
      edges: JSON.parse(JSON.stringify(previousState.edges)), // Deep clone
      viewport: { ...previousState.viewport },
      globalTags: JSON.parse(JSON.stringify(previousState.globalTags)), // Deep clone
      globalDomains: JSON.parse(JSON.stringify(previousState.globalDomains || [])), // Deep clone
      historyIndex: newIndex,
      isDirty: true,
      isLoading: true, // Prevent re-saving to history
      selectedNode: null, // Clear selection
      selectedNodes: [],
      selectedEdge: null,
      highlightedEdges: [],
      highlightedNodes: []
    });
    
    // Reset loading flag
    setTimeout(() => {
      set({ isLoading: false });
    }, 100);
    
    toast.success('Action undone');
  },
  
  canUndo: () => {
    const state = get();
    return state.historyIndex > 0;
  },
  
  clearHistory: () => {
    set({
      history: [],
      historyIndex: -1
    });
  },
  
  // Get current model data for saving
  getModelData: () => {
    const state = get();
    return {
      name: state.modelName,
      type: state.modelType,
      description: state.modelDescription,
      businessRules: state.modelBusinessRules,
      nodes: state.nodes,
      edges: state.edges,
      viewport: state.viewport,
      globalTags: state.globalTags,
      globalDomains: state.globalDomains,
      userTypes: state.userTypes
    };
  }
}));

export default useFlowStore;
